import type { BrandfetchLogoChoice, CreativeContextState, ImageCardPreview } from '@/types/creative-library';
import type {
  CreativeSubject,
  CreativeSubjectBody,
  UpdateCreativeSubjectBody,
  CreativeBoard,
  CreativeBoardBody,
  CreativeBrand,
  CreativeBrandBody,
  CreativeProduct,
  CreativeProductBody,
  CreativeItem,
  CreativeItemBody,
  CreativeSharing,
  CreativeSourceDefaults,
  CreativeMaterialRole,
  CreativeContextSelection,
  UpdateCreativeBoardBody,
  UpdateCreativeBrandBody,
  UpdateCreativeProductBody,
  UpdateCreativeItemBody,
} from "@digital-twins/shared-types";
import api from "./client";

async function list<T>(
  path: string,
  params?: Record<string, string | undefined>,
): Promise<T[]> {
  return (await api.get<{ items: T[] }>(path, { params })).data.items;
}
export const creativeLibraryApi = {
  subjects: (brandToken?: string) =>
    list<CreativeSubject>("/library/subjects", { brandToken }),
  subject: async (token: string) =>
    (await api.get<CreativeSubject>(`/library/subjects/${token}`)).data,
  createSubject: async (body: CreativeSubjectBody) =>
    (await api.post<CreativeSubject>("/library/subjects", body)).data,
  updateSubject: async (token: string, body: UpdateCreativeSubjectBody) =>
    (await api.patch<CreativeSubject>(`/library/subjects/${token}`, body)).data,
  brands: () => list<CreativeBrand>("/library/brands"),
  createBrand: async (body: CreativeBrandBody) =>
    (await api.post<CreativeBrand>("/library/brands", body)).data,
  updateBrand: async (token: string, body: UpdateCreativeBrandBody) =>
    (await api.patch<CreativeBrand>(`/library/brands/${token}`, body)).data,
  products: (brandToken?: string) =>
    list<CreativeProduct>("/library/products", { brandToken }),
  product: async (token: string) =>
    (await api.get<CreativeProduct>(`/library/products/${token}`)).data,
  createProduct: async (body: CreativeProductBody) =>
    (await api.post<CreativeProduct>("/library/products", body)).data,
  updateProduct: async (token: string, body: UpdateCreativeProductBody) =>
    (await api.patch<CreativeProduct>(`/library/products/${token}`, body)).data,
  board: async (token: string) =>
    (await api.get<CreativeBoard>(`/library/boards/${token}`)).data,
  boards: (brandToken?: string, channel?: string) =>
    list<CreativeBoard>("/library/boards", { brandToken, channel }),
  createBoard: async (body: CreativeBoardBody) =>
    (await api.post<CreativeBoard>("/library/boards", body)).data,
  updateBoard: async (token: string, body: UpdateCreativeBoardBody) =>
    (await api.patch<CreativeBoard>(`/library/boards/${token}`, body)).data,
  downloadCreative: async (token: string) =>
    (
      await api.get<Blob>(`/library/creatives/${token}/download`, {
        responseType: "blob",
      })
    ).data,
  creative: async (token: string) =>
    (await api.get<CreativeItem>(`/library/creatives/${token}`)).data,
  creatives: (brandToken?: string, boardToken?: string) =>
    list<CreativeItem>("/library/creatives", { brandToken, boardToken }),
  createCreative: async (body: CreativeItemBody) =>
    (await api.post<CreativeItem>("/library/creatives", body)).data,
  updateCreative: async (token: string, body: UpdateCreativeItemBody) =>
    (await api.patch<CreativeItem>(`/library/creatives/${token}`, body)).data,
  archive: async (
    kind: "brands" | "boards" | "products" | "subjects" | "creatives",
    token: string,
  ) => {
    await api.delete(`/library/${kind}/${token}`);
  },
  upload: async (body: FormData) =>
    (await api.post<CreativeItem>("/library/creatives/upload", body)).data,
  importBrandfetchLogos: async (body: {
    domain: string;
    logos: Array<BrandfetchLogoChoice>;
    brandToken?: string | null;
  }) =>
    (
      await api.post<Array<CreativeItem>>(
        "/library/creatives/brandfetch-logos",
        body,
      )
    ).data,
  sourceDefaults: async (assetToken: string) =>
    (
      await api.get<CreativeSourceDefaults>(
        `/library/creatives/source/${assetToken}`,
      )
    ).data,
  saveThreadAsset: async (body: {
    assetToken: string;
    title: string;
    role: CreativeMaterialRole;
    brandToken?: string | null;
    productToken?: string | null;
  }) =>
    (await api.post<CreativeItem>("/library/creatives/from-thread-asset", body))
      .data,
  sharing: async (token: string) =>
    (await api.get<CreativeSharing>(`/library/creatives/${token}/sharing`))
      .data,
  setSharing: async (token: string, sharingMode: string) =>
    (await api.patch(`/library/creatives/${token}/sharing`, { sharingMode }))
      .data,
  addCollaborator: async (token: string, userToken: string) =>
    (
      await api.post(`/library/creatives/${token}/sharing/collaborators`, {
        userToken,
      })
    ).data,
  removeCollaborator: async (token: string, userToken: string) => {
    await api.delete(
      `/library/creatives/${token}/sharing/collaborators/${userToken}`,
    );
  },
  context: async (
    token: string,
  ): Promise<CreativeContextState> =>
    (await api.get(`/knowledge/threads/${token}/creative-context`)).data,
  previewImageCard: async (
    token: string,
    messageToken: string,
    selection: CreativeContextSelection,
  ): Promise<ImageCardPreview> =>
    (
      await api.post(
        `/knowledge/threads/${token}/creative-context/preview`,
        { messageToken, selection },
      )
    ).data,
  updateContext: async (
    token: string,
    revision: number,
    selection: CreativeContextSelection,
  ): Promise<CreativeContextState> =>
    (
      await api.patch(`/knowledge/threads/${token}/creative-context`, {
        revision,
        selection,
      })
    ).data,
};
